import { cn } from "@/utils/cn";

export type BristolOption = { value: string; label: string; description: string };

type Props = {
  id: string;
  label: string;
  options: BristolOption[];
  value: unknown;
  onChange: (v: string) => void;
  error?: string;
};

function BristolVisual({ type }: { type: string }) {
  const box = "mx-auto flex h-10 w-14 items-center justify-center rounded border border-slate-300 bg-white";
  switch (type) {
    case "type1":
      return (
        <div className={box}>
          <div className="flex gap-0.5">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className="h-1.5 w-1.5 rounded-full bg-slate-600" />
            ))}
          </div>
        </div>
      );
    case "type2":
      return (
        <div className={box}>
          <div className="h-6 w-10 rounded-full border-2 border-slate-600 border-dotted" />
        </div>
      );
    case "type3":
      return (
        <div className={box}>
          <div className="h-7 w-10 rounded-md border-2 border-dashed border-slate-600" />
        </div>
      );
    case "type4":
      return (
        <div className={box}>
          <div className="h-7 w-12 rounded-full bg-slate-500" />
        </div>
      );
    case "type5":
      return (
        <div className={box}>
          <div className="flex gap-0.5">
            {[0, 1, 2].map((i) => (
              <span key={i} className="h-2 w-3 rounded-sm bg-slate-500" />
            ))}
          </div>
        </div>
      );
    case "type6":
      return (
        <div className={box}>
          <div className="h-8 w-12 rounded-sm border border-slate-400 bg-slate-200" style={{ clipPath: "polygon(10% 0,100% 0,90% 100%,0 100%)" }} />
        </div>
      );
    case "type7":
      return (
        <div className={box}>
          <div className="h-4 w-14 rounded-sm bg-sky-200" />
        </div>
      );
    default:
      return <div className={box} />;
  }
}

export function FieldBristolChart({ id, label, options, value, onChange, error }: Props) {
  const v = typeof value === "string" ? value : "";
  return (
    <div className="mb-4">
      <span id={`${id}-legend`} className="choice-group__legend block">{label}</span>
      <div
        className="flex flex-wrap gap-2 overflow-x-auto pb-1"
        role="radiogroup"
        aria-labelledby={`${id}-legend`}
        id={id}
      >
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={v === opt.value}
            onClick={() => onChange(opt.value)}
            className={cn(
              "flex min-w-[7.5rem] max-w-[9rem] flex-shrink-0 flex-col gap-1 rounded-lg border-2 p-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-healthsource-navy focus-visible:ring-offset-2",
              v === opt.value
                ? "border-healthsource-navy bg-[#f0f4f9]"
                : "border-[#e2e2ec] bg-white hover:border-healthsource-navy"
            )}
          >
            <BristolVisual type={opt.value} />
            <span className="text-[15px] font-bold text-healthsource-dark">{opt.label}</span>
            <span className="text-[13px] leading-snug text-healthsource-gray">{opt.description}</span>
          </button>
        ))}
      </div>
      {error && <p className="choice-group__error" role="alert">{error}</p>}
    </div>
  );
}
